import { useCallback, useEffect, useRef, useState } from 'react';
import type { Product } from '../types/Product';

interface WorkerResult<T> {
  data: T;
  error?: string;
}

export const useQuickBuyWorker = () => {
  const workerRef = useRef<Worker | null>(null);
  const [isWorkerSupported, setIsWorkerSupported] = useState(true);
  const pendingCallbacks = useRef<Map<string, (result: any) => void>>(new Map());
  const messageIdCounter = useRef(0);

  // Initialize worker on mount
  useEffect(() => {
    if (typeof Worker !== 'undefined') {
      try {
        // Create worker with Vite's special import syntax
        workerRef.current = new Worker(
          new URL('../workers/quickBuy.worker.ts', import.meta.url),
          { type: 'module' }
        );

        // Set up message handler
        workerRef.current.onmessage = (event) => {
          const { type, error, messageId } = event.data;
          
          // Handle responses with messageId for request-response pattern
          if (messageId && pendingCallbacks.current.has(messageId)) {
            const callback = pendingCallbacks.current.get(messageId);
            pendingCallbacks.current.delete(messageId);
            callback?.(event.data);
            return;
          }

          // Handle broadcast messages
          if (type === 'ERROR') {
            console.error('Worker error:', error);
          }
        };

        workerRef.current.onerror = (error) => {
          console.error('Worker error:', error);
          setIsWorkerSupported(false);
        };
      } catch (error) {
        console.warn('Web Worker not supported:', error);
        setIsWorkerSupported(false);
      }
    } else {
      setIsWorkerSupported(false);
    }

    // Cleanup
    return () => {
      workerRef.current?.terminate();
      pendingCallbacks.current.clear();
    };
  }, []);

  // Send message to worker with promise-based response
  const sendToWorker = useCallback(<T>(type: string, data: any): Promise<WorkerResult<T>> => {
    return new Promise((resolve) => {
      const messageId = `${type}_${++messageIdCounter.current}`;
      
      if (workerRef.current && isWorkerSupported) {
        // Use worker
        pendingCallbacks.current.set(messageId, (result) => {
          resolve({ data: result.data, error: result.error });
        });
        
        workerRef.current.postMessage({ type, data, messageId });
      } else {
        // Fallback to main thread
        setTimeout(() => {
          try {
            const result = performFallbackCalculation(type, data);
            resolve({ data: result as T });
          } catch (error) {
            resolve({ 
              data: null as T, 
              error: error instanceof Error ? error.message : 'Unknown error' 
            });
          }
        }, 0);
      }
    });
  }, [isWorkerSupported]);

  // Filter products
  const filterProducts = useCallback(async (
    products: Product[],
    searchTerm: string,
    selectedBrand: string,
    selectedCategory: string
  ) => {
    const result = await sendToWorker<{
      filteredProducts: Product[];
      inStockProducts: Product[];
      outOfStockProducts: Product[];
    }>('FILTER_PRODUCTS', {
      products,
      searchTerm,
      selectedBrand,
      selectedCategory
    });
    
    return result.data || { 
      filteredProducts: [], 
      inStockProducts: [], 
      outOfStockProducts: [] 
    };
  }, [sendToWorker]);

  // Calculate totals
  const calculateTotals = useCallback(async (
    cart: Array<{ id: string; price: number; quantity: number }>,
    discountPercentage: number
  ) => {
    const result = await sendToWorker<{
      subtotal: number;
      totalItems: number;
      totalSavings: number;
    }>('CALCULATE_TOTALS', {
      cart,
      discountPercentage
    });
    
    return result.data || { subtotal: 0, totalItems: 0, totalSavings: 0 };
  }, [sendToWorker]);

  // Extract filters
  const extractFilters = useCallback(async (products: Product[]) => {
    const result = await sendToWorker<{
      brands: string[];
      categories: string[];
    }>('EXTRACT_FILTERS', products);
    
    return result.data || { brands: [], categories: [] };
  }, [sendToWorker]);

  return {
    isWorkerSupported,
    filterProducts,
    calculateTotals,
    extractFilters
  };
};

// Fallback calculations for non-worker environments
function performFallbackCalculation(type: string, data: any): any {
  switch (type) {
    case 'FILTER_PRODUCTS': {
      const { products, searchTerm, selectedBrand, selectedCategory } = data;
      const filtered = products.filter((product: Product) => {
        const matchesSearch = !searchTerm || 
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesBrand = !selectedBrand || 
          (product.brand || 'DemoCrackers') === selectedBrand;
        
        const matchesCategory = !selectedCategory || 
          product.category === selectedCategory;
        
        return matchesSearch && matchesBrand && matchesCategory;
      });
      
      return {
        filteredProducts: filtered,
        inStockProducts: filtered.filter((p: Product) => p.inStock),
        outOfStockProducts: filtered.filter((p: Product) => !p.inStock)
      };
    }
    
    case 'CALCULATE_TOTALS': {
      const { cart, discountPercentage } = data;
      const calculateDiscountedPrice = (price: number) => {
        return price - (price * discountPercentage / 100);
      };
      
      const subtotal = cart.reduce((sum: number, item: any) => {
        return sum + (calculateDiscountedPrice(item.price) * item.quantity);
      }, 0);

      const totalSavings = cart.reduce((sum: number, item: any) => {
        return sum + ((item.price * discountPercentage / 100) * item.quantity);
      }, 0);

      const totalItems = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);

      return { subtotal, totalItems, totalSavings };
    }
    
    case 'EXTRACT_FILTERS': {
      const products = data as Product[];
      const brandsSet = new Set<string>();
      const categoriesSet = new Set<string>();
      
      products.forEach(product => {
        brandsSet.add(product.brand || 'DemoCrackers');
        if (product.category) {
          categoriesSet.add(product.category);
        }
      });
      
      return {
        brands: Array.from(brandsSet).sort(),
        categories: Array.from(categoriesSet).sort()
      };
    }
    
    default:
      throw new Error(`Unknown calculation type: ${type}`);
  }
}